/* eslint-disable react/jsx-no-target-blank */
import React from 'react';
import { Trans } from '@lingui/macro';

import { Box, Flex, Link, Paragraph, Text } from '@rover/kibble/core';
import Section from '@rover/kibble/patterns/Section';
import HowRoverWorks, { ImageOptions } from '@rover/react-lib/src/components/HowRoverWorks';
import LandingHero, {
  HeroImageSrcSet,
} from '@rover/react-lib/src/pages/landing/components/LandingHero';
import Props from '@rover/react-lib/src/pages/landing/ServiceLanding/pages/ServiceLandingPageProps';
import AppStoreCallout from '@rover/react-lib/src/pages/shared/AppStoreCallout/AppStoreCallout';
import CitiesAndNeighborhoods from '@rover/react-lib/src/pages/shared/CitiesAndNeighborhoods/CitiesAndNeighborhoods';
import {
  SERVICE_TYPE_SLUG_FOR_SERVICE_LANDING_ENUM,
  ServiceLandingEnum,
} from '@rover/types/src/ServiceLanding';

import LandingHelmet from '../../../components/LandingHelmet';
import { getSearchFiltersFromSearchResults } from '../../../utilities';
import Guarantees from '../../components/Guarantees';
import ServiceLandingResultsLayout from '../../components/ServiceLandingResultsLayout';
import ServiceLandingServices from '../../components/ServiceLandingServices';
import ServiceTargetAudience from '../../components/ServiceTargetAudience';
import ServiceValueProposition from '../../components/ServiceValueProposition';
import puppyBoardingHeroImageDesktop from '../shared-images/Hero_Puppy_desktop.jpg';
import puppyBoardingHeroImageMobile from '../shared-images/Hero_Puppy_mobile.jpg';
import puppyBoardingHeroImageTablet from '../shared-images/Hero_Puppy_tablet.jpg';

const puppyBoardingImages: HeroImageSrcSet = {
  desktop: {
    src: puppyBoardingHeroImageDesktop,
    height: 927,
  },
  tablet: {
    src: puppyBoardingHeroImageTablet,
    height: 927,
  },
  mobile: {
    src: puppyBoardingHeroImageMobile,
    height: 614,
  },
};

const PuppyBoarding: React.FC<Props> = ({
  language,
  countryConfig,
  serviceLinksWithDisplayName,
  serviceLandingData,
  citiesAndNeighborhoods,
  searchResults,
  frontendConfigSettings,
  adwordslocation,
  serviceTypes,
}) => {
  const serviceLandingType = ServiceLandingEnum.PUPPY_BOARDING;
  const serviceTypeSlug = SERVICE_TYPE_SLUG_FOR_SERVICE_LANDING_ENUM[serviceLandingType];

  const { h1, h2, seoTitle, seoMetaDescription } = serviceLandingData;

  const initialSearchFilters = getSearchFiltersFromSearchResults(serviceLandingType, searchResults);

  return (
    <>
      <LandingHelmet title={seoTitle} metaDescription={seoMetaDescription} />
      <LandingHero mb="20x" heroImageSrcSet={puppyBoardingImages}>
        <LandingHero.Content>
          <LandingHero.Display>{h1}</LandingHero.Display>
          <LandingHero.Text>{h2}</LandingHero.Text>
          <LandingHero.SearchForm
            language={language}
            serviceTypes={serviceTypes}
            serviceTypeSlug={serviceTypeSlug}
            initialSearchFilters={initialSearchFilters}
          />
          <LandingHero.TrustPilot languageCode={language} />
        </LandingHero.Content>
      </LandingHero>
      {searchResults && searchResults.results.length && (
        <ServiceLandingResultsLayout
          serviceLandingType={serviceLandingType}
          language={language}
          countryConfig={countryConfig}
          searchResults={searchResults}
          mapSettings={frontendConfigSettings}
          adwordslocation={adwordslocation}
        />
      )}
      <Section mt="0x" mb="40x">
        <Flex flexDirection="column" alignItems="center">
          <ServiceValueProposition>
            <ServiceValueProposition.Display>
              <Trans>Find affordable long-term puppy boarding</Trans>
            </ServiceValueProposition.Display>
            <ServiceValueProposition.List>
              <ServiceValueProposition.ListItem>
                <Text fontWeight="semibold">
                  <Trans>Affordable, personalized puppy care</Trans>
                </Text>
                <Paragraph mb="8x">
                  <Trans>
                    We make it easy to search for a sitter who is more affordable than a kennel.
                    Find out{' '}
                    <Link href="/dog-sitting-rates/" target="_blank">
                      how much
                    </Link>{' '}
                    puppy sitters near you typically cost.
                  </Trans>
                </Paragraph>
              </ServiceValueProposition.ListItem>
              <ServiceValueProposition.ListItem>
                <Text fontWeight="semibold">
                  <Trans>Puppy sitters you can trust</Trans>
                </Text>
                <Paragraph mb="8x">
                  <Trans>
                    Every sitter on the site has been reviewed and approved by the Rover team, and
                    you can easily search for sitters who offer puppy care.
                  </Trans>
                </Paragraph>
              </ServiceValueProposition.ListItem>
              <ServiceValueProposition.ListItem>
                <Text fontWeight="semibold">
                  <Trans>Convenient Meet & Greets</Trans>
                </Text>
                <Paragraph mb="8x">
                  <Trans>
                    Meet your puppy’s perfect match before you book. Ask any sitters you’re
                    interested in to set up a free Meet & Greet.
                  </Trans>
                </Paragraph>
              </ServiceValueProposition.ListItem>
              <ServiceValueProposition.ListItem>
                <Text fontWeight="semibold">
                  <Trans>Cute photo updates</Trans>
                </Text>
                <Paragraph mb="8x">
                  <Trans>
                    Get photos of your puppy’s clumsy steps, snuggles with their sitter, and
                    evidence that they’re having a blast.
                  </Trans>
                </Paragraph>
              </ServiceValueProposition.ListItem>
            </ServiceValueProposition.List>
          </ServiceValueProposition>

          <HowRoverWorks>
            <HowRoverWorks.Display>
              <Trans>Meet local sitters who will treat your puppy like family</Trans>
            </HowRoverWorks.Display>
            <HowRoverWorks.List>
              <HowRoverWorks.ListItem image={ImageOptions.MAGNIFYING_GLASS}>
                <HowRoverWorks.ItemHeading step={1}>
                  <Trans>Search</Trans>
                </HowRoverWorks.ItemHeading>
                <HowRoverWorks.ItemContent>
                  <Trans>
                    Read verified reviews by pet parents like you and choose a sitter who’s a great
                    match for you and your puppy.
                  </Trans>
                </HowRoverWorks.ItemContent>
              </HowRoverWorks.ListItem>
              <HowRoverWorks.ListItem image={ImageOptions.PHONE}>
                <HowRoverWorks.ItemHeading step={2}>
                  <Trans>Book and Pay</Trans>
                </HowRoverWorks.ItemHeading>
                <HowRoverWorks.ItemContent>
                  <Trans>
                    No cash or checks needed—we make it simple to book and make secured payments
                    through our website or app.
                  </Trans>
                </HowRoverWorks.ItemContent>
              </HowRoverWorks.ListItem>
              <HowRoverWorks.ListItem image={ImageOptions.COLLAR}>
                <HowRoverWorks.ItemHeading step={3}>
                  <Trans>Relax</Trans>
                </HowRoverWorks.ItemHeading>
                <HowRoverWorks.ItemContent>
                  <Trans>
                    Your puppy is in great hands with a loving sitter. Plus, your booking is backed
                    by{' '}
                    <Link href={countryConfig.urls.supportUrl} target="_blank">
                      24/7 support
                    </Link>{' '}
                    and the{' '}
                    <Link href={countryConfig.roverGuaranteeUrl} target="_blank">
                      Rover Guarantee
                    </Link>
                    .
                  </Trans>
                </HowRoverWorks.ItemContent>
              </HowRoverWorks.ListItem>
            </HowRoverWorks.List>
          </HowRoverWorks>

          <ServiceTargetAudience>
            <ServiceTargetAudience.Display>
              <Trans>Puppy sitting that works for you, your puppy, and your budget</Trans>
            </ServiceTargetAudience.Display>
            <ServiceTargetAudience.Content>
              <Trans>
                Founded in 2011, Rover makes it easy for you to connect with a loving puppy sitter
                in your neighborhood. If you're looking for someone who can keep up with your
                puppy—without breaking the bank—you're in the right place.
              </Trans>
            </ServiceTargetAudience.Content>
          </ServiceTargetAudience>

          <Guarantees serviceTypeSlug={serviceTypeSlug} countryConfig={countryConfig} />

          <ServiceLandingServices {...{ serviceLandingType, serviceLinksWithDisplayName }}>
            <ServiceLandingServices.Display>
              <Trans>Rover offers many services to meet your pet care needs, including:</Trans>
            </ServiceLandingServices.Display>
          </ServiceLandingServices>

          <CitiesAndNeighborhoods citiesOrNeighborhoods={citiesAndNeighborhoods}>
            <CitiesAndNeighborhoods.Display>
              <Trans>Thousands of pet sitters in cities and neighborhoods, including:</Trans>
            </CitiesAndNeighborhoods.Display>
          </CitiesAndNeighborhoods>

          <Box my="8x">
            <AppStoreCallout />
          </Box>
        </Flex>
      </Section>
    </>
  );
};

export default PuppyBoarding;
